import React from 'react';
import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Slide,
  Toolbar,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';

interface HeaderProps {
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
  align?: 'left' | 'center';
  serviceCount?: number;
  onBadgeClick?: () => void;
  rightSlot?: React.ReactNode; // allows passing custom actions
  sticky?: boolean;            // new: sticks to top using AppBar
  elevateOnScroll?: boolean;   // new: dynamic elevation on scroll
}

export const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  showBackButton = false,
  onBack,
  align = 'center',
  serviceCount = 0,
  onBadgeClick,
  rightSlot,
  sticky = false,
  elevateOnScroll = false,
}) => {
  const isCenter = align === 'center';

  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  const content = (
    <Toolbar sx={{ minHeight: 64 }}>
      <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
        {showBackButton && (
          <IconButton onClick={onBack} sx={{ mr: 0.5 }} aria-label="Go back">
            <ArrowBackIcon />
          </IconButton>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            textAlign: isCenter ? 'center' : 'left',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', mt: 0.25, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={0.5}>
        {rightSlot}
        {serviceCount > 0 && onBadgeClick && (
          <IconButton
            onClick={onBadgeClick}
            aria-label={`View summary of ${serviceCount} added services`}
            size="large"
          >
            <Badge
              badgeContent={serviceCount}
              color="primary"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.75rem',
                  padding: '0 6px',
                  height: 'auto',
                  minWidth: 20,
                  borderRadius: '10px',
                },
              }}
            >
              <CollectionsBookmarkIcon />
            </Badge>
          </IconButton>
        )}
      </Box>
    </Toolbar>
  );

  if (sticky) {
    return (
      <Slide appear={false} direction="down" in>
        <AppBar
          position="sticky"
          elevation={elevateOnScroll && trigger ? 3 : 0}
          color="default"
          sx={{
            bgcolor: 'background.paper',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {content}
        </AppBar>
      </Slide>
    );
  }

  // non-sticky fallback (keeps your old layout behavior, now with Toolbar spacing)
  return (
    <Box
      bgcolor="background.paper"
      position="relative"
      zIndex={1}
      sx={{
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      {content}
    </Box>
  );
};

export default Header;
